import React, { FC } from 'react';
import styled, { css } from 'styled-components';
import useConfig from '@empiriecom/module-components/hooks/useConfig';
import { Price } from '@empiriecom/module-components/Price';
import { FragmentConfiguration } from '@/config';
import { Product } from '@/interfaces';

type ProductInformationProps = {
  animationState: string;
  product: Product;
  isMobile: boolean;
};

const animationStyles = {
  entered: css`
    height: auto;
    max-height: 6.25rem;
  `,
  entering: css`
    height: auto;
    max-height: 0;
  `,
  exited: css`
    height: auto;
    max-height: 0;
  `,
  exiting: css`
    height: auto;
    max-height: 6.25rem;
  `,
};

const AnimationWrapper = styled.div<{
  isMobile: boolean;
  hasAnimation: boolean;
  animationState: string;
}>`
  display: inline-flex;
  justify-content: flex-start;
  align-items: flex-start;
  overflow: hidden;
  ${({ hasAnimation }) => hasAnimation && 'transition: all 400ms ease-in-out;'}
  ${({ isMobile }) =>
    isMobile &&
    `height: auto;
    max-height: 0;
    margin-bottom: 0;`}
  ${({ animationState }) => animationStyles[animationState]}
`;

const ProductImage = styled.div`
  margin-right: 2rem;
  width: 4.75rem;
  height: 4.75rem;
  img {
    width: 100%;
    height: auto;
  }
`;

const ProductData = styled.div`
  display: inline-flex;
  justify-content: flex-start;
  align-items: flex-start;
  flex-direction: column;
  &&.mobile {
    align-self: flex-end;
    margin-bottom: 0.5em;
  }
`;

const ProductName = styled.div`
  font-size: 0.875rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
`;

export const ProductInformation: FC<ProductInformationProps> = ({
  animationState,
  isMobile,
  product,
}): JSX.Element => {
  const [imageSrc] = product.images;
  const {
    price: { value, currency, valueOld, unit, valueUnit, amount, uvp, isStartPrice },
    name,
  } = product;
  const {
    price: { layout },
  } = useConfig<FragmentConfiguration>();
  return (
    <AnimationWrapper isMobile={isMobile} hasAnimation={isMobile} animationState={animationState}>
      <ProductImage>
        <img
          data-testid="sticky-product-image"
          src={imageSrc.concat('?$PDV_gallery$')}
          alt={name}
        />
      </ProductImage>
      <ProductData className={isMobile && 'mobile'}>
        <ProductName data-testid="sticky-product-name">{!isMobile && name}</ProductName>
        <div data-testid="sticky-price">
          <Price
            amount={amount}
            currency={currency}
            isMSRP={uvp}
            isStartPrice={isStartPrice}
            layout={layout}
            oldPrice={valueOld}
            price={value}
            savingPrefix
            unit={unit}
            unitPrice={valueUnit}
          />
        </div>
      </ProductData>
    </AnimationWrapper>
  );
};

export default ProductInformation;
